import React, { useState } from 'react';
import {
  Box,
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import ROUTE_PATH from '../../config/routes.config';
import { setAdminUserLoggedIn } from '../../utils/local-cache.util';
import validationRules from '../../utils/validations.util';
import { getDeviceOs, getDeviceModel } from '../../utils/device-info.util';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { adminUserLogin } from '../../services/auth.service';
import { APP_THEME } from '../../theme/theme-colors';

const Login = () => {
  const {
    control,
    resetField,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      username: data.username.replace(/\s+/g, ' ').trim(),
      password: data.password,
      deviceDetails: {
        os: getDeviceOs(),
        model: getDeviceModel(),
      },
    };
    adminUserLogin(payload)
      .then((res) => {
        const loggedInUserPayload = {
          token: res.data?.auth?.token || '',
          name: res.data?.name || '',
          email: res.data?.email || '',
          role: res.data?.role || '',
        };
        setAdminUserLoggedIn(loggedInUserPayload);

        window.location.href = ROUTE_PATH.DASHBOARD;
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        resetField('password');
        setFormSubmitted(false);
      });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack spacing={3} sx={{ my: 2 }}>
        <Controller
          name="username"
          control={control}
          rules={validationRules.REQUIRED}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email"
              placeholder="Enter your email"
              error={!!errors.username}
              helperText={errors?.username?.message || null}
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="solar:letter-bold-duotone" width={20} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'rgba(244, 246, 248, 0.72)',
                },
              }}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={validationRules.REQUIRED}
          render={({ field }) => (
            <TextField
              {...field}
              label="Password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              error={!!errors.password}
              helperText={errors?.password?.message || null}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="solar:lock-password-bold-duotone"
                      width={20}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      <Iconify
                        icon={
                          showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'rgba(244, 246, 248, 0.72)',
                },
              }}
            />
          )}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Authorized users only
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            bgcolor: 'rgba(2, 79, 160, 0.08)',
            color: APP_THEME,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          Secure access
        </Box>
      </Stack>

      <Button
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        disabled={formSubmitted}
        sx={{
          py: 1.5,
          borderRadius: 3,
          bgcolor: APP_THEME,
          boxShadow: '0 14px 30px rgba(2, 79, 160, 0.28)',
          textTransform: 'none',
          fontSize: 16,
          fontWeight: 700,
          '&:hover': {
            bgcolor: '#013f80',
          },
        }}
      >
        Sign in
      </Button>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </form>
  );
};

export default Login;
